import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Briefcase, Activity, HardDrive, Clock, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { archiveService } from '../services/archive.service';
import { commissionService } from '../services/commission.service';
import { userService } from '../services/user.service';
import { formatBytes } from '../utils/format.utils';
import Header from '../components/Header';
import DocumentCard from '../components/DocumentCard';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [archives, setArchives] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [myArchives, setMyArchives] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError('');

        // Récupérer les données en parallèle
        const [allArchivesData, myArchivesData, commissionsData, statsData] = await Promise.all([
          archiveService.getArchives({
            page: 0,
            size: 6,
            sort: 'createdAt,desc',
          }),
          archiveService.getMyArchives({
            page: 0,
            size: 100,
          }),
          commissionService.getActiveCommissions(),
          userService.getMyStats().catch(() => null), // Ne pas bloquer si l'endpoint n'existe pas encore
        ]);

        setArchives(allArchivesData.content || []);
        setMyArchives(myArchivesData.content || []);
        setCommissions(commissionsData || []);
        setUserStats(statsData);
      } catch (error) {
        console.error('Erreur lors du chargement du dashboard:', error);
        setError(error.message || 'Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Calculer les stats
  const stats = {
    totalDocuments: archives.length,
    totalCommissions: commissions.length,
    recentActivities: myArchives.length,
    storageUsed: userStats?.storageUsed || 0, // En octets depuis le backend
  };

  const recentDocuments = archives.slice(0, 3);

  const statCards = [
    {
      title: 'Total Archives',
      value: stats.totalDocuments,
      icon: FileText,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      title: 'Commissions',
      value: stats.totalCommissions,
      icon: Briefcase,
      iconBg: 'bg-primary-50',
      iconColor: 'text-primary-600',
    },
    {
      title: 'Mes Archives',
      value: stats.recentActivities,
      icon: Activity,
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600',
    },
    {
      title: 'Stockage utilisé',
      value: formatBytes(stats.storageUsed),
      icon: HardDrive,
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-600',
    },
  ];

  // État de chargement
  if (loading) {
    return (
      <div>
        <Header title="Dashboard" />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement du dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // État d'erreur
  if (error) {
    return (
      <div>
        <Header title="Dashboard" />
        <div className="px-8">
          <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-lg p-6 mt-8">
            <div className="flex items-start gap-3 text-red-800">
              <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold mb-1">Erreur de chargement</h3>
                <p className="text-sm">{error}</p>
              </div>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="p-4">
        {/* Message de bienvenue */}
        <div className="mb-6 bg-white rounded-xl p-5 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">👋</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Bonjour, {user?.firstName} !
              </h2>
              <p className="text-sm text-gray-600">
                Gérez vos archives facilement
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-1">
            Vue d'ensemble
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-sm p-4 border border-gray-200 active:scale-95 transition-transform"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2.5 rounded-lg ${stat.iconBg}`}>
                      <Icon className={stat.iconColor} size={20} />
                    </div>
                  </div>
                  <h4 className="text-gray-500 text-xs font-medium mb-1">
                    {stat.title}
                  </h4>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-3 px-1">
            <div className="flex items-center gap-2">
              <Clock className="text-primary-600" size={18} />
              <h3 className="text-base font-semibold text-gray-800">
                Récentes
              </h3>
            </div>
            <button
              onClick={() => navigate('/documents')}
              className="text-primary-600 hover:text-primary-700 font-medium text-sm active:scale-95 transition-transform"
            >
              Tout voir
            </button>
          </div>

          {recentDocuments.length > 0 ? (
            <div className="space-y-3">
              {recentDocuments.map((doc) => (
                <DocumentCard key={doc.id} document={doc} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <FileText className="mx-auto text-gray-300 mb-4" size={48} />
              <p className="text-gray-500 mb-4">Aucune archive disponible</p>
              <button
                onClick={() => navigate('/documents/add')}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Créer une archive
              </button>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg shadow-lg p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2">
                Bienvenue dans votre espace d'archivage
              </h3>
              <p className="text-primary-100">
                Gérez et organisez toutes les archives du Touba Rennes
              </p>
            </div>
            <button
              onClick={() => navigate('/documents/add')}
              className="px-6 py-3 bg-white text-primary-700 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
            >
              Ajouter une archive
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
